
import {
  signInWithEmailAndPassword,
  signOut,
  createUserWithEmailAndPassword,
  updateProfile,
  onAuthStateChanged,
  sendPasswordResetEmail
} from "firebase/auth";
import { auth, db } from "./config";
import {
  doc,
  getDoc,
  setDoc,
  collection,
  query,
  where,
  getDocs,
  serverTimestamp
} from "firebase/firestore";

// ==========================================
// 🔐 DEFAULT PERMISSIONS PER ROLE
// ==========================================
function getDefaultPermissions(role) {
  const perms = {
    admin: {
      viewLeads: true, addLeads: true, editLeads: true, deleteLeads: true,
      viewCalls: true, addCalls: true, viewTasks: true, manageTasks: true,
      viewReports: true, manageTeam: true, accountingAccess: true, settingsAccess: true
    },
    manager: {
      viewLeads: true, addLeads: true, editLeads: true, deleteLeads: false,
      viewCalls: true, addCalls: true, viewTasks: true, manageTasks: true,
      viewReports: true, manageTeam: false, accountingAccess: false, settingsAccess: false
    },
    sales: {
      viewLeads: true, addLeads: true, editLeads: false, deleteLeads: false,
      viewCalls: true, addCalls: true, viewTasks: true, manageTasks: false,
      viewReports: false, manageTeam: false, accountingAccess: false, settingsAccess: false
    },
    accountant: {
      viewLeads: false, addLeads: false, editLeads: false, deleteLeads: false,
      viewCalls: false, addCalls: false, viewTasks: true, manageTasks: true,
      viewReports: true, manageTeam: false, accountingAccess: true, settingsAccess: false
    }
  };
  return perms[role] || perms.sales;
}

// ==========================================
// 🔐 LOGIN
// ==========================================
export const loginUser = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    console.log("✅ Firebase Auth SUCCESS:", user.uid, user.email);

    // Firestore document check karo
    const userDoc = await getDoc(doc(db, "users", user.uid));

    if (!userDoc.exists()) {
      console.log("⚠️ Auth OK but Firestore document MISSING, auto-creating...");

      await setDoc(doc(db, "users", user.uid), {
        name: user.displayName || user.email.split("@")[0],
        email: user.email,
        role: "sales",
        phone: "",
        isActive: true,
        joinedAt: serverTimestamp(),
        permissions: getDefaultPermissions("sales")
      });

      console.log("✅ Auto-created Firestore document");

      return {
        uid: user.uid,
        email: user.email,
        name: user.displayName || user.email.split("@")[0],
        role: "sales"
      };
    }

    const userData = userDoc.data();
    console.log("✅ Firestore document FOUND:", userData.name, userData.role);

    return {
      uid: user.uid,
      email: user.email,
      ...userData
    };

  } catch (error) {
    console.error("❌ Login Error:", error.code, error.message);

    if (error.code === "auth/user-not-found") {
      throw new Error("Account nahi mila! Firebase Console mein account banao.");
    } else if (error.code === "auth/wrong-password") {
      throw new Error("Password galat hai!");
    } else if (error.code === "auth/invalid-email") {
      throw new Error("Email format galat hai!");
    } else if (error.code === "auth/too-many-requests") {
      throw new Error("Bahut zyada tries! Thoda wait karo.");
    } else if (error.code === "auth/operation-not-allowed") {
      throw new Error("Email/Password login enable nahi hai! Firebase Console → Authentication → Email/Password ON karo.");
    } else if (error.code === "auth/network-request-failed") {
      throw new Error("Internet connection check karo!");
    } else if (error.code === "auth/invalid-credential") {
      throw new Error("Email ya password galat hai! Sahi details try karo.");
    } else {
      throw new Error(`Login failed: ${error.message}`);
    }
  }
};

// ==========================================
// 🔐 REGISTER (standalone - if needed)
// ==========================================
export const registerUser = async (userData) => {
  const { name, email, password, role, phone } = userData;

  const userCredential = await createUserWithEmailAndPassword(auth, email, password);
  const user = userCredential.user;

  await updateProfile(user, { displayName: name });

  await setDoc(doc(db, "users", user.uid), {
    name,
    email,
    password: password,
    role: role || "sales",
    phone: phone || "",
    isActive: true,
    joinedAt: serverTimestamp(),
    permissions: getDefaultPermissions(role || "sales")
  });

  return { uid: user.uid, email, password, name, role: role || "sales", phone };
};

// ==========================================
// 🔐 ADD TEAM MEMBER (from Admin Panel)
// ==========================================
export const addTeamMember = async (memberData) => {
  const { name, email, password, role, phone } = memberData;

  try {
    console.log("👤 Creating team member:", email, role);

    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    await updateProfile(user, { displayName: name });
    console.log("✅ Firebase Auth user created:", user.uid);

    await setDoc(doc(db, "users", user.uid), {
      name,
      email,
      password: password,
      role: role || "sales",
      phone: phone || "",
      isActive: true,
      joinedAt: serverTimestamp(),
      permissions: getDefaultPermissions(role || "sales")
    });

    console.log("✅ Firestore document created for:", email);

    return { uid: user.uid, email, password, name, role: role || "sales", phone };
  } catch (error) {
    console.error("❌ Add Team Member Error:", error.code, error.message);

    if (error.code === "auth/email-already-in-use") {
      throw new Error("Ye email pehle se registered hai! Doosra email try karo.");
    } else if (error.code === "auth/invalid-email") {
      throw new Error("Email format galat hai!");
    } else if (error.code === "auth/weak-password") {
      throw new Error("Password kam se kam 6 characters hona chahiye!");
    } else {
      throw new Error(`Failed: ${error.message}`);
    }
  }
};

// ==========================================
// 🔐 LOGOUT
// ==========================================
export const logoutUser = async () => {
  await signOut(auth);
};

// ==========================================
// 🔐 AUTH STATE LISTENER
// ==========================================
export const authStateListener = (callback) => {
  return onAuthStateChanged(auth, async (user) => {
    console.log("🔄 Auth state changed:", user ? user.email : "null");

    if (user) {
      try {
        const userDoc = await getDoc(doc(db, "users", user.uid));

        if (userDoc.exists()) {
          const userData = userDoc.data();
          console.log("✅ Auth listener - doc found:", userData.name, userData.role);
          callback({
            uid: user.uid,
            email: user.email,
            ...userData
          });
        } else {
          console.log("⚠️ Auth listener - doc MISSING, using defaults");
          callback({
            uid: user.uid,
            email: user.email,
            name: user.displayName || user.email.split("@")[0],
            role: "sales"
          });
        }
      } catch (error) {
        console.error("⚠️ Auth listener Firestore error:", error.message);
        callback({
          uid: user.uid,
          email: user.email,
          name: user.displayName || user.email.split("@")[0],
          role: "sales"
        });
      }
    } else {
      console.log("🚫 Auth listener - No user");
      callback(null);
    }
  });
};

// ==========================================
// 🔐 RESET PASSWORD
// ==========================================
export const resetPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    return "✅ Password reset email sent!";
  } catch (error) {
    throw new Error(`Failed: ${error.message}`);
  }
};

// ==========================================
// 🔐 GET USER TASKS
// ==========================================
export const getCurrentUserTasks = async (userId) => {
  try {
    const snap = await getDocs(
      query(
        collection(db, "tasks"),
        where("assignedTo", "==", userId)
      )
    );
    return snap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    }));
  } catch (error) {
    console.error("Get Tasks Error:", error);
    return [];
  }
};